import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize} from 'rxjs/operators';
import {DocumentApiService, DocumentDTO} from '../api/document-api.service';
import {SpinnerService} from '../shared/services/spinner.service';
import {ToastrService} from 'ngx-toastr';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const PAGE_SIZE = 20;

@Component({
    selector: 'app-admin-documents',
    templateUrl: './admin-documents.component.html',
    styleUrls: ['./admin-documents.component.css']
})
export class AdminDocumentsComponent implements OnInit, OnDestroy {
    /** True when shown inside a chapter workspace (keeps its sidebar) rather than as its own page. */
    @Input() embedded = false;
    selectedFile: File | null = null;
    lastUpload: DocumentDTO | null = null;
    uploading = false;
    documents: DocumentDTO[] = [];
    loadingDocuments = false;
    loadingMore = false;
    userRole: any;
    chapterId = '';
    searchTerm = '';
    cursor: string | null = null;
    hasMore = false;
    isDragging = false;

    private searchChanged = new Subject<string>();

    constructor(
        private documentApiService: DocumentApiService,
        private spinner: SpinnerService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit(): void {
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.chapterId = sessionStorage.getItem('chapter') || '';

        this.searchChanged.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(() => this.loadDocuments());

        if (this.userRole?.DOCUMENT?.includes('READ')) {
            this.loadDocuments();
        }
    }

    ngOnDestroy(): void {
        this.searchChanged.complete();
    }

    onSearchChange(term: string): void {
        this.searchTerm = term;
        this.searchChanged.next(term);
    }

    loadDocuments(): void {
        this.loadingDocuments = true;
        this.cursor = null;
        this.documentApiService.list(this.chapterId, this.searchTerm, undefined, PAGE_SIZE).pipe(
            finalize(() => this.loadingDocuments = false)
        ).subscribe({
            next: (page) => {
                this.documents = page.documents;
                this.cursor = page.nextCursor;
                this.hasMore = page.hasMore;
            },
            error: (err) => {
                console.error('Error loading documents', err);
                this.toastr.error('Failed to load documents');
            }
        });
    }

    loadMore(): void {
        if (!this.hasMore || this.loadingMore) {
            return;
        }
        this.loadingMore = true;
        this.documentApiService.list(this.chapterId, this.searchTerm, this.cursor ?? undefined, PAGE_SIZE).pipe(
            finalize(() => this.loadingMore = false)
        ).subscribe({
            next: (page) => {
                this.documents = [...this.documents, ...page.documents];
                this.cursor = page.nextCursor;
                this.hasMore = page.hasMore;
            },
            error: (err) => {
                console.error('Error loading more documents', err);
                this.toastr.error('Failed to load more documents');
            }
        });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files && input.files.length ? input.files[0] : null;
        this.lastUpload = null;
    }

    clearSelectedFile(): void {
        this.selectedFile = null;
        this.lastUpload = null;
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
    }

    onFileDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
        const file = event.dataTransfer?.files?.[0];
        if (file) {
            this.selectedFile = file;
            this.lastUpload = null;
        }
    }

    upload(): void {
        if (!this.selectedFile) {
            return;
        }
        if (this.selectedFile.size > MAX_FILE_SIZE_BYTES) {
            this.toastr.error('File exceeds the 10MB size limit');
            return;
        }

        this.spinner.show();
        this.uploading = true;
        this.documentApiService.upload(this.selectedFile, this.chapterId).pipe(
            finalize(() => {
                this.spinner.hide();
                this.uploading = false;
            })
        ).subscribe({
            next: (result) => {
                this.lastUpload = result;
                this.toastr.success('Document uploaded');
                this.loadDocuments();
            },
            error: (err) => {
                console.error('Error uploading document', err);
                const message = typeof err?.error === 'string' ? err.error : 'Failed to upload document';
                this.toastr.error(message);
            }
        });
    }

    deleteDocument(doc: DocumentDTO): void {
        if (!confirm(`Delete "${doc.filename}"? This cannot be undone.`)) {
            return;
        }

        this.spinner.show();
        this.documentApiService.delete(doc.id).pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: () => {
                this.toastr.success('Document deleted');
                this.loadDocuments();
            },
            error: (err) => {
                console.error('Error deleting document', err);
                const message = typeof err?.error === 'string' ? err.error : 'Failed to delete document';
                this.toastr.error(message);
            }
        });
    }

    formatSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    trackByDocId(index: number, doc: DocumentDTO): string {
        return doc.id;
    }

    copyUrl(url: string): void {
        if (!url) {
            return;
        }

        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(url).then(
                () => this.toastr.success('URL copied to clipboard'),
                () => this.toastr.error('Failed to copy URL')
            );
        } else {
            this.legacyCopy(url);
        }
    }

    // navigator.clipboard is undefined outside a secure context (plain HTTP, e.g. UAT
    // without HTTPS) — falls back to the deprecated execCommand path so Copy still works.
    private legacyCopy(text: string): void {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                this.toastr.success('URL copied to clipboard');
            } else {
                this.toastr.error('Failed to copy URL');
            }
        } catch {
            this.toastr.error('Failed to copy URL');
        } finally {
            document.body.removeChild(textarea);
        }
    }
}
